"use client"

// 错误边界必须是客户端组件；无 layout 上下文，多语言用 pathname 解析 + 本地文案
import { usePathname } from "next/navigation"

interface GlobalErrorProps {
	error: Error & { digest?: string }
}

/** 支持的语言（与 i18n/request 的 locales 一致），global-error 内无法用 next-intl，故本地维护文案 */
const LOCALES = ["en", "th", "id", "es", "fr", "pt", "ru", "vn", "ko"] as const
type Locale = (typeof LOCALES)[number]

const messages: Record<Locale, { title: string; retry: string }> = {
	en: { title: "Something went wrong!", retry: "Try again" },
	th: { title: "เกิดข้อผิดพลาด", retry: "ลองอีกครั้ง" },
	id: { title: "Terjadi kesalahan", retry: "Coba lagi" },
	es: { title: "Algo salió mal", retry: "Reintentar" },
	fr: { title: "Une erreur s'est produite", retry: "Réessayer" },
	pt: { title: "Algo deu errado", retry: "Tentar novamente" },
	ru: { title: "Произошла ошибка", retry: "Повторить" },
	vn: { title: "Đã xảy ra lỗi", retry: "Thử lại" },
	ko: { title: "문제가 발생했습니다", retry: "다시 시도" },
}

const pageStyles = {
	html: { minHeight: "100%" },
	body: {
		margin: 0,
		minHeight: "100vh",
		fontFamily: "system-ui, sans-serif",
		padding: "2rem",
		textAlign: "center" as const,
		backgroundColor: "#f5f5f5",
		display: "flex",
		flexDirection: "column" as const,
		alignItems: "center",
		justifyContent: "center",
		boxSizing: "border-box" as const,
	},
	title: { color: "#333", marginBottom: "1rem", marginTop: 0 },
	primaryBtn: {
		padding: "0.5rem 1rem",
		fontSize: "1rem",
		cursor: "pointer" as const,
		backgroundColor: "#d7000f",
		color: "#fff",
		border: "none",
		borderRadius: "4px",
	},
}

function getLocaleFromPathname(pathname: string | null): Locale {
	if (!pathname) return "en"
	const segment = pathname.split("/")[1]
	return LOCALES.includes(segment as Locale) ? (segment as Locale) : "en"
}

/**
 * 全局错误边界：捕获未处理错误后展示本页；多语言根据 URL 首段解析，无额外 DOM。
 */
export default function GlobalError(_props: GlobalErrorProps) {
	const pathname = usePathname()
	const locale = getLocaleFromPathname(pathname)
	const t = messages[locale]

	const handleRetry = () => {
		window.location.reload()
	}

	return (
		<html lang={locale === "en" ? "en" : locale} style={pageStyles.html}>
			<body style={pageStyles.body}>
				<h2 style={pageStyles.title}>{t.title}</h2>
				<button type="button" onClick={handleRetry} style={pageStyles.primaryBtn}>
					{t.retry}
				</button>
			</body>
		</html>
	)
}
